import { QueryClient, useQueryClient } from '@tanstack/react-query'
import { createOrganization, updateOrganization } from 'api/account'
import { handleFormSubmit } from 'components/formHandlers'
import { useEffect, useState } from 'react'
import { useForm, UseFormReset } from 'react-hook-form'
import { mapOrganizationToDTO, Organization, OrganizationDTO } from 'types/Organization'

export interface UseOrganizationFormProps {
    initialData?: Organization | null
    queryKey?: string[]
    onClose?: () => void
}

interface OnSubmitProps {
    initialData?: Organization | null
    queryClient: QueryClient
    queryKey: string[]
    onClose?: () => void
    reset: UseFormReset<OrganizationDTO>
    setLoading: (loading: boolean) => void
}

export const handleOrganizationFormSubmit = async (props: OnSubmitProps, data: OrganizationDTO) => {
    try {
        await handleFormSubmit<Organization, OrganizationDTO, 'org_id', string>(
            {
                ...props,
                createEntity: createOrganization,
                updateEntity: updateOrganization,
                entityIdKey: 'org_id',
            },
            data,
        )
    } catch (error) {
        throw error
    }
}

export const useOrganizationForm = ({ initialData, queryKey = ['organizations'], onClose }: UseOrganizationFormProps) => {
    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
        setError,
    } = useForm<OrganizationDTO>({
        defaultValues: initialData ? mapOrganizationToDTO(initialData) : {},
    })

    const queryClient = useQueryClient()
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (initialData) {
            const mappedData = mapOrganizationToDTO(initialData)
            Object.keys(mappedData).forEach((key) => {
                setValue(key as keyof OrganizationDTO, mappedData[key as keyof OrganizationDTO])
            })
        }
    }, [initialData, setValue])

    const onSubmit = async (data: OrganizationDTO) => {
        setLoading(true)
        try {
            await handleOrganizationFormSubmit(
                {
                    initialData,
                    queryClient,
                    queryKey,
                    onClose,
                    reset,
                    setLoading,
                },
                data,
            )
        } catch (error: any) {
            setLoading(false)
            if (error?.message?.includes('email')) {
                setError('email', { type: 'manual', message: 'Ez az email cím már regisztrálva van a rendszerben.' })
            } else {
                console.error('Unexpected error:', error)
            }
        }
    }

    return {
        control,
        handleSubmit: handleSubmit(onSubmit),
        errors,
        loading,
    }
}
